import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, Chip, CardMedia, Divider, Skeleton, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Comments from '../components/blog/Comments';
import PostEngagement from '../components/blog/PostEngagement';
import { getPost } from '../services/api';
import { BlogPost as blogPostType } from '../types/BlogPost';
import dayjs from 'dayjs';
import { SnackbarProvider, enqueueSnackbar } from 'notistack';


const BlogPost = () => {
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [post, setPost] = useState<blogPostType>();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const navigate = useNavigate();

  const fetchPosts = async (id: string, incrementViews: boolean = true) => {
    try {
      setIsLoading(true)
      const { data }: any = await getPost(id, incrementViews);
      data.id = data._id;
      setPost(data as any);
    } catch (error) {
      enqueueSnackbar((error as any).message, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchPosts(id as string);
  }, []);

  if(isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Skeleton variant="rectangular" width={100} height={40} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="80%" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Skeleton variant="rectangular" width={80} height={32} />
            <Skeleton variant="rectangular" width={60} height={32} />
            <Skeleton variant="rectangular" width={70} height={32} />
          </Box>
          <Skeleton variant="rectangular" width="100%" height={400} sx={{ mb: 4, borderRadius: 1 }} />
          <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="95%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="85%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="70%" height={24} sx={{ mb: 4 }} />
          <Divider sx={{ my: 4 }} />
          <Box sx={{ mb: 4 }}>
            <Skeleton variant="rectangular" width={200} height={40} />
          </Box>
          <Box sx={{ mb: 4 }}>
            <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={100} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={100} sx={{ mb: 2 }} />
          </Box>
        </Box>
      </Container>
    )
  }

  if (!post) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ my: 4 }}>
          Post not found
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/posts')}
          variant="contained"
        >
          Back to Posts
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        
        <Typography variant="h3" component="h1" gutterBottom>
          {post.title}
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            By {post.authorId.username} • {dayjs(post.publishDate).format("DD-MM-YYYY")}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={post.categoryId.name} color="primary" />
            {post.tags.map(tag => (
              <Chip key={tag} label={tag} variant="outlined" />
            ))}
          </Box>
        </Box>

        <CardMedia
          component="img"
          image={post.featuredImage}
          alt={post.title}
          sx={{ 
            width: '100%',
            height: 'auto',
            maxHeight: '750px',
            objectPosition: 'top',
            objectFit: 'cover',
            borderRadius: 1,
            mb: 4
          }}
        />

        <Typography 
          variant="body1" 
          sx={{ whiteSpace: 'pre-line', mb: 4 }} 
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <PostEngagement
            postId={post.id}
            initialLikes={post.likes}
            initialViews={post.views}
            setPost={setPost}
            fetchPosts={fetchPosts}
          />
        </Box>

        <Comments postId={post.id} />
      </Box>
      <SnackbarProvider />
    </Container>
  );
};

export default BlogPost; 